import React, { useState } from 'react';

/**
 * SpiderRadar - Live updating radar/spider chart for Knowledge Weights
 * Mobile-optimized with proper padding and short labels
 */
const SpiderRadar = ({ values, labels, size = 320, onHover }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  // Short labels for chart display
  const defaultShortLabels = [
    'Product',
    'Direction', 
    'Promo',
    'Ambassador',
    'Values',
    'Compliance',
    'Viral',
    'Trend'
  ];
  
  // Full labels for tooltips
  const fullLabels = [
    'Product Core',
    'Campaign Direction',
    'Promotion',
    'Ambassador Tone',
    'Company Values',
    'Compliance Guard',
    'Viral Heuristics',
    'External Trend'
  ];
  
  const chartLabels = labels || defaultShortLabels;
  
  // Extract values in order
  const dataValues = [
    values.product_core || 0,
    values.campaign_direction || 0,
    values.promotion || 0,
    values.ambassador_tone || 0,
    values.company_values || 0,
    values.compliance_guard || 0,
    values.viral_heuristics || 0,
    values.external_trend || 0
  ];
  
  // Responsive padding based on size
  const isMobile = size < 360;
  const padding = isMobile ? 45 : 50;
  const center = size / 2;
  const radius = (size / 2) - padding; // Increased padding to prevent clipping
  const levels = 5;
  const angleStep = (Math.PI * 2) / dataValues.length;
  
  // Calculate point on radar
  const getPoint = (value, index) => {
    const angle = (index * angleStep) - (Math.PI / 2); // Start at top
    const r = (value / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };
  
  // Generate polygon path
  const getPolygonPath = () => {
    return dataValues
      .map((value, i) => {
        const point = getPoint(value, i);
        return `${i === 0 ? 'M' : 'L'} ${point.x},${point.y}`;
      })
      .join(' ') + ' Z';
  };
  
  // Generate grid circles
  const gridCircles = [];
  for (let i = 1; i <= levels; i++) {
    const r = (radius / levels) * i;
    gridCircles.push(
      <circle
        key={`grid-${i}`}
        cx={center}
        cy={center}
        r={r}
        fill="none"
        stroke="#E0E0E0"
        strokeWidth="1"
      />
    );
  }
  
  // Generate axis lines
  const axisLines = dataValues.map((_, i) => {
    const point = getPoint(100, i);
    return (
      <line
        key={`axis-${i}`}
        x1={center}
        y1={center}
        x2={point.x}
        y2={point.y}
        stroke="#E0E0E0"
        strokeWidth="1"
      />
    );
  });
  
  // Generate labels with responsive positioning
  const labelElements = chartLabels.map((label, i) => {
    const angle = (i * angleStep) - (Math.PI / 2);
    const labelRadius = radius + (isMobile ? 18 : 22); // Closer on mobile
    const x = center + labelRadius * Math.cos(angle);
    const y = center + labelRadius * Math.sin(angle);
    const value = dataValues[i];
    const isHovered = hoveredIndex === i;
    
    // Adjust text anchor based on position
    let textAnchor = 'middle';
    if (x < center - 5) textAnchor = 'end';
    if (x > center + 5) textAnchor = 'start';
    
    // Adjust baseline based on vertical position
    let dominantBaseline = 'middle';
    if (y < center - 5) dominantBaseline = 'auto';
    if (y > center + 5) dominantBaseline = 'hanging';
    
    return (
      <g 
        key={`label-${i}`}
        onMouseEnter={() => setHoveredIndex(i)}
        onMouseLeave={() => setHoveredIndex(null)}
        style={{ cursor: 'pointer' }}
      >
        {/* Label text */}
        <text
          x={x}
          y={y}
          textAnchor={textAnchor}
          dominantBaseline={dominantBaseline}
          fontSize={isMobile ? "10" : "11"}
          fontWeight="600"
          fill={isHovered ? "#0e67a2" : "#333"}
          style={{ transition: 'fill 0.2s' }}
        >
          {label}
        </text>
        
        {/* Tooltip on hover */}
        {isHovered && (
          <g>
            <rect
              x={x - 40}
              y={y - 25}
              width="80"
              height="20"
              fill="#333"
              rx="4"
              opacity="0.9"
            />
            <text
              x={x}
              y={y - 12}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#fff"
            >
              {fullLabels[i]}
            </text>
            <text
              x={x}
              y={y - 28}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
              fill="#0e67a2"
            >
              {value}%
            </text>
          </g>
        )}
      </g>
    );
  });
  
  // Generate vertex points with hover states
  const vertexPoints = dataValues.map((value, i) => {
    const point = getPoint(value, i);
    const isHovered = hoveredIndex === i;
    
    return (
      <g 
        key={`vertex-${i}`}
        onMouseEnter={() => setHoveredIndex(i)}
        onMouseLeave={() => setHoveredIndex(null)}
        onTouchStart={() => setHoveredIndex(i)}
        onTouchEnd={() => setHoveredIndex(null)}
        style={{ cursor: 'pointer' }}
        role="button"
        aria-label={`${fullLabels[i]} ${value} percent`}
        tabIndex={0}
      >
        <circle
          cx={point.x}
          cy={point.y}
          r={isHovered ? "6" : "4"}
          fill="#0e67a2"
          stroke="#fff"
          strokeWidth="2"
          style={{ transition: 'r 0.2s' }}
        />
      </g>
    );
  });
  
  return (
    <svg
      width={size}
      height={size}
      style={{
        maxWidth: '100%',
        height: 'auto',
        overflow: 'visible' // Allow labels outside viewBox
      }}
      role="img"
      aria-label="Spider chart showing knowledge weight distribution"
    >
      {/* Grid circles */}
      {gridCircles}
      
      {/* Axis lines */}
      {axisLines}
      
      {/* Data polygon */}
      <path
        d={getPolygonPath()}
        fill="rgba(0, 122, 255, 0.25)"
        stroke="#0e67a2"
        strokeWidth="2"
        strokeLinejoin="round"
        style={{ transition: 'd 0.3s ease-out' }}
      />
      
      {/* Vertex points */}
      {vertexPoints}
      
      {/* Labels */}
      {labelElements}
    </svg>
  );
};

export default SpiderRadar;
